import { useState, useEffect, useRef, useCallback } from "react";
import {
  KEYCODE_MAP,
  KEYCODE_MACRO_MAP,
  WEB_KEY_TO_KEYCODE_MAP,
} from "@eweadn-qiankun/shared-components";

interface UseKeyboardTestResult {
  pressedKeys: Array<{ id: number; label: string }>;
  releasedKeys: Array<{ id: number; label: string }>;
  getTargetKeyLabel: (item: any) => string[];
}

export function useKeyboardTest(options?: {
  enabled?: boolean;
}): UseKeyboardTestResult {
  const enabled = options?.enabled !== false;
  // kcs → 显示名（仅用于已配置列表，不参与按键测试）
  const getTargetKeyLabel = (item: any): string[] => {
    const kcs = item?.data?.kcs;
    if (kcs == null) return [];
    const toLabel = (kc: number) =>
      KEYCODE_MAP[kc]?.name || KEYCODE_MACRO_MAP[kc]?.name || String(kc);
    if (Array.isArray(kcs)) {
      return kcs.filter((kc: number) => kc !== 0).map(toLabel);
    }
    return kcs !== 0 ? [toLabel(kcs)] : [];
  };

  const getDefaultLabels = useCallback((code: string): string[] => {
    const keycode = WEB_KEY_TO_KEYCODE_MAP[code];
    if (keycode != null) {
      return [
        KEYCODE_MAP[keycode]?.name || KEYCODE_MACRO_MAP[keycode]?.name || code,
      ];
    }
    return [code];
  }, []);

  const idCounterRef = useRef(0);
  const heldCodesRef = useRef<Set<string>>(new Set());
  const releasedTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const [pressedKeys, setPressedKeys] = useState<
    Array<{ id: number; label: string }>
  >([]);
  const [releasedKeys, setReleasedKeys] = useState<
    Array<{ id: number; label: string }>
  >([]);

  // 按键测试只展示物理按键原始标签，不套用高级键映射（浏览器无法区分物理键盘）
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      e.preventDefault();

      const code = e.code;
      heldCodesRef.current.add(code);

      const labels: string[] = [];
      for (const c of heldCodesRef.current) {
        labels.push(...getDefaultLabels(c));
      }

      const newIds = labels.map(() => idCounterRef.current++);
      setPressedKeys(labels.map((label, i) => ({ id: newIds[i], label })));
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      e.preventDefault();

      const code = e.code;
      heldCodesRef.current.delete(code);

      // 刷新按下列表
      const labels: string[] = [];
      for (const c of heldCodesRef.current) {
        labels.push(...getDefaultLabels(c));
      }
      const newIds = labels.map(() => idCounterRef.current++);
      setPressedKeys(labels.map((label, i) => ({ id: newIds[i], label })));

      // 抬起条目
      const upLabels = getDefaultLabels(code);
      const upIds = upLabels.map(() => idCounterRef.current++);
      setReleasedKeys((prev) => [
        ...prev,
        ...upLabels.map((label, i) => ({ id: upIds[i], label })),
      ]);
      upIds.forEach((rid) => {
        const timer = setTimeout(() => {
          setReleasedKeys((prev) => prev.filter((k) => k.id !== rid));
          releasedTimersRef.current.delete(rid);
        }, 2000);
        releasedTimersRef.current.set(rid, timer);
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      releasedTimersRef.current.forEach((t) => clearTimeout(t));
    };
  }, [enabled, getDefaultLabels]);

  return { pressedKeys, releasedKeys, getTargetKeyLabel };
}
